import { Component, computed, effect, HostBinding, Input, OnInit, ViewChild } from '@angular/core';
import {
  AgentKeys,
  AGMedia,
  AGMediaKeys,
  Conference,
  ConferenceKeys,
  ElasticSearchAgent,
  EMAIL_ACTION_TRIGGERS_LOOKUP,
  EmailAddressKeys,
  EmailTemplateConfiguration,
  EmailTemplateConfigurationKeys,
  EmailTemplates,
  EmailTemplatesDetailsMap,
  EmailTemplatesModels,
  EVENTS_EMAIL_TEMPLATES,
  LookupKeys,
  Registrant,
  RegistrantKeys,
  RegistrantModelKeys,
  SENDER_CONFIGURATION_MAP,
} from 'ag-common-lib/public-api';
import { AgentSearchService } from 'ag-common-svc/lib/services/agent-search';
import DataSource from 'devextreme/data/data_source';
import { getAgentFullName } from 'ag-common-svc/lib/services/agent.service/agent-service-utils';
import CustomStore from 'devextreme/data/custom_store';
import { DxFilterOperators } from 'ag-common-svc/lib/services/elastic-search.services/base-elastic-search-service';
import { AgentEmailAddressesService, AuthService, ConferenceRegistrantsService } from 'ag-common-svc/public-api';
import { ColumnButtonClickEvent, RowRemovingEvent } from 'devextreme/ui/data_grid';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import {
  EventInquiryRequest,
  EventInquiryRequestKeys,
} from 'ag-common-lib/lib/models/registration/event-inquiry-request.model';
import { ModalWindowComponent } from 'ag-common-svc/lib/components/modal-window/modal-window.component';
import { BehaviorSubject, filter, firstValueFrom, mergeMap, Observable, shareReplay } from 'rxjs';
import { ConferenceModalService } from '../conference-form.service';
import { EventInquiryRequestsService } from 'ag-common-svc/lib/services/event-inquire-requests.service';
import { AttendeesEmailsService } from 'src/app/ag-crm/registrations/attendees-emails.service';
import { ConferenceRegistrationEmailCampaignService } from 'ag-common-svc/lib/services/email-campaigns/conference-registration-email-campaign.service';
import { EmailTemplateConfigurationFormService } from './email-template-configuration-form.service';
import { ConferenceEmailTestEmailService, ConferenceTestEmailFormData } from './conference-email-test-email.service';
import { MediaUploaderTabs } from 'ag-common-svc/lib/components/ag-media-uploader/ag-media-uploader-modal.models';
import { AgMediaUploaderModalComponent } from 'ag-common-svc/lib/components/ag-media-uploader/ag-media-uploader-modal.component';
import { TemplatesService } from 'ag-common-svc/lib/services/templates.service';
import {
  Template,
  TemplateExcursionsSet,
  TemplateKeys,
  TemplateRequiredField,
  TemplateRequiredFieldTypes,
  TemplateTypes,
} from 'ag-common-svc/lib/components/administration/template-builder/template-editor-page/models';
import { QueryParam, WhereFilterOperandKeys } from 'ag-common-svc/lib/dao/CommonFireStoreDao.dao';
import { EmailTemplateModel, NotificationOptInGroupType } from 'ag-common-lib/lib';

type TemplateRequiredFieldView = TemplateRequiredField & { isStatic?: boolean };
import { ChairmanExtensionConfigurationKeys } from 'ag-common-lib/lib/models/registration/conference-chairman-extension';

@Component({
  selector: 'ag-crm-conference-email-sender-form',
  templateUrl: './conference-email-sender-form.component.html',
  styleUrls: ['./conference-email-sender-form.component.scss'],
  providers: [
    ConferenceRegistrationEmailCampaignService,
    AttendeesEmailsService,
    EmailTemplateConfigurationFormService,
    ConferenceEmailTestEmailService,
  ],
})
export class ConferenceEmailSenderFormComponent implements OnInit {
  @HostBinding('class') className = 'conference-email-sender-form';
  @ViewChild('sendTestEmailModalRef', { static: true }) sendTestEmailModalComponent: ModalWindowComponent;
  @ViewChild('editEmailTemplateModalRef', { static: false }) editEmailTemplateModalComponent: ModalWindowComponent;
  @ViewChild('uploadAttachmentModalRef', { static: false }) mediaUploaderComponent: AgMediaUploaderModalComponent;
  @Input()
  set conference(value: Partial<Conference>) {
    this._conference = value;
    this.emailTemplatesDataSource?.reload();
  }
  get conference(): Partial<Conference> {
    return this._conference;
  }
  @Input() validationGroup: string;
  @Input() isReadonlyMode: boolean = false;

  protected readonly LookupKeys = LookupKeys;
  protected readonly AgentKeys = AgentKeys;
  protected readonly BaseModelKeys = BaseModelKeys;
  protected readonly ConferenceKeys = ConferenceKeys;
  protected readonly ChairmanExtensionConfigurationKeys = ChairmanExtensionConfigurationKeys;
  protected readonly EmailTemplatesModels = EmailTemplatesModels;
  protected readonly RegistrantModelKeys = RegistrantModelKeys;
  protected readonly RegistrantKeys = RegistrantKeys;
  protected readonly EventInquiryRequestKeys = EventInquiryRequestKeys;
  protected readonly EmailTemplateConfigurationKeys = EmailTemplateConfigurationKeys;
  protected readonly MediaUploaderTabs = MediaUploaderTabs;
  protected readonly EmailTemplates = EmailTemplates;
  protected readonly AGMediaKeys = AGMediaKeys;
  protected readonly TemplateRequiredFieldTypes = TemplateRequiredFieldTypes;
  protected readonly emailActionTriggersLookup = EMAIL_ACTION_TRIGGERS_LOOKUP;
  protected readonly emailSendersLookup = SENDER_CONFIGURATION_MAP;
  protected readonly excursionsSetLookup = [
    { [LookupKeys.value]: TemplateExcursionsSet.main, [LookupKeys.description]: 'Main' },
    { [LookupKeys.value]: TemplateExcursionsSet.chairmanExtension, [LookupKeys.description]: 'Chairman Extension' },
    { [LookupKeys.value]: TemplateExcursionsSet.all, [LookupKeys.description]: 'All' },
  ];
  protected emailTemplatesLookup: Array<{
    key: string;
    items: Array<{ value: EmailTemplates | string; key?: string; description: string }>;
  }> = [...EVENTS_EMAIL_TEMPLATES];
  protected emailTemplatesLookupFlat = this.flattenEmailTemplatesLookup(this.emailTemplatesLookup);
  private _conference: Partial<Conference>;
  private dynamicTemplatesLoaded$ = new BehaviorSubject<boolean>(false);
  protected emailTemplatesDataSource = new DataSource({
    store: new CustomStore({
      key: EmailTemplateConfigurationKeys.templateName,
      loadMode: 'raw',
      load: async () => {
        await firstValueFrom(this.dynamicTemplatesLoaded$.pipe(filter(Boolean)));
        return this._conference?.[ConferenceKeys.emailTemplates] ?? [];
      },
      insert: value => {
        const templates = this._conference?.[ConferenceKeys.emailTemplates] ?? [];
        templates.push(value);
        if (this._conference) {
          this._conference[ConferenceKeys.emailTemplates] = templates;
        }
        return Promise.resolve(null);
      },
      update: (key, values) => {
        const templates = this._conference?.[ConferenceKeys.emailTemplates] ?? [];
        const index = templates.findIndex(item => item?.[EmailTemplateConfigurationKeys.templateName] === key);
        if (index >= 0) {
          Object.assign(templates[index], values);
        }
        return Promise.resolve(null);
      },
      remove: key => {
        const templates = this._conference?.[ConferenceKeys.emailTemplates] ?? [];
        const filtered = templates.filter(item => item?.[EmailTemplateConfigurationKeys.templateName] !== key);
        if (this._conference) {
          this._conference[ConferenceKeys.emailTemplates] = filtered;
        }
        return Promise.resolve(null);
      },
    }),
  });
  protected readonly agentsEmailAddressesDataSource = this.agentEmailAddressesService.agentsEmailAddressesDataSource;
  protected dataSource = new DataSource({
    paginate: true,
    pageSize: 250,
    store: new CustomStore({
      key: 'dbId',
      loadMode: 'processed',
      load: async loadOptions => {
        const filter: any[] = [];

        if (loadOptions.filter) {
          this.conference?.[ConferenceKeys.bccEmail]?.forEach((item: any) => {
            if (filter?.length) {
              filter.push('or');
            }
            filter.push([AgentKeys.email_addresses, DxFilterOperators.contains, item?.address]);
          });
        }

        if (loadOptions.searchValue) {
          [AgentKeys.email_addresses, AgentKeys.p_agent_first_name, AgentKeys.p_agent_last_name].forEach(field => {
            if (filter?.length) {
              filter.push('or');
            }

            filter.push([field, DxFilterOperators.contains, loadOptions.searchValue]);
          });
        }

        loadOptions.filter = filter;
        const data = [];
        const response = await this.agentSearchService.search(loadOptions);

        response?.data?.forEach((agent: ElasticSearchAgent) => {
          const fullName = getAgentFullName(agent);
          agent?.[AgentKeys.email_addresses]?.forEach(emailAddress => {
            const address = emailAddress?.[EmailAddressKeys.address];
            data.push({
              name: fullName,
              address: address,
            });
          });
        });

        return {
          // One entry per address, so the agent count is not the row count. The count
          // this used to read came from an aggregation the request never asked for, and
          // was undefined on every call.
          data,
          totalCount: data.length,
        };
      },
    }),
  });

  protected emailTemplateConfigurationFormDataSig = this.emailTemplateConfigurationFormService.formDataSig;

  private dynamicTemplatesMap = new Map<string, Template>();
  private readonly dynamicTemplatePrefix = 'dynamic:';

  protected conferenceRegistrants$: Observable<Registrant[]> = this.conferenceModalService.conferenceDbId$.pipe(
    filter(Boolean),
    mergeMap(conferenceDbId => {
      return this.conferenceRegistrantsService.getRegistrantsByConferenceId(conferenceDbId);
    }),
    shareReplay(1),
  );

  protected eventInquiryRequests$: Observable<EventInquiryRequest[]> = this.conferenceModalService.conferenceDbId$.pipe(
    mergeMap(conferenceDbId => {
      return this.eventInquiryRequestsService.getList(conferenceDbId);
    }),
    shareReplay(1),
  );

  protected saveEmailConfigurationInProgress$ = this.emailTemplateConfigurationFormService.inProgress$;
  protected sendTestEmailInProgress$ = this.conferenceEmailTestEmailService.inProgress$;
  protected testEmailFormData: ConferenceTestEmailFormData;

  protected readonly templateRequiredFieldsSet = computed(() => {
    const emailTemplateConfigurationFormData = this.emailTemplateConfigurationFormDataSig();
    const templateName = emailTemplateConfigurationFormData?.[EmailTemplateConfigurationKeys.templateName];
    if (!templateName) {
      return [];
    }

    let fields: TemplateRequiredFieldView[] = [];

    if (this.isDynamicTemplateName(templateName)) {
      const templateId = this.getDynamicTemplateId(templateName);
      const template = templateId ? this.dynamicTemplatesMap.get(templateId) : null;
      fields = (template?.[TemplateKeys.requiredFields] ?? []).map(field => ({ ...field }));
      return fields;
    }

    if (this.isActivitiesTemplate(templateName)) {
      fields = [
        {
          widgetId: 'static-activities-day',
          type: TemplateRequiredFieldTypes.activitiesDay,
          label: 'Activities Day',
          isStatic: true,
        },
      ];
      return fields;
    }

    return [];
  });

  constructor(
    private authService: AuthService,
    private conferenceRegistrantsService: ConferenceRegistrantsService,
    private eventInquiryRequestsService: EventInquiryRequestsService,
    private conferenceModalService: ConferenceModalService,
    private agentSearchService: AgentSearchService,
    private agentEmailAddressesService: AgentEmailAddressesService,
    private emailTemplateConfigurationFormService: EmailTemplateConfigurationFormService,
    private templatesService: TemplatesService,
    private conferenceEmailTestEmailService: ConferenceEmailTestEmailService,
  ) {
    effect(
      () => {
        const fields = this.templateRequiredFieldsSet();
        const formData = this.emailTemplateConfigurationFormDataSig();
        this.ensureRequiredFieldsPayload(fields, formData);
      },
      { allowSignalWrites: true },
    );
  }

  async ngOnInit() {
    await this.loadDynamicEmailTemplates();
  }

  protected attachmentsCalculateDisplayValue = (emailTemplateConfiguration: EmailTemplateConfiguration) => {
    const attachments = emailTemplateConfiguration?.[EmailTemplateConfigurationKeys.attachments];

    if (!attachments?.length) {
      return null;
    }

    return `${attachments?.length} attachment(s)`;
  };

  protected handlerSendTestEmail = async (e: ColumnButtonClickEvent<EmailTemplateConfiguration>) => {
    const emailTemplateConfiguration: EmailTemplateConfiguration = e?.row?.data;
    const agent = await firstValueFrom(this.authService.loggedInAgent$);
    const primaryEmail = agent?.[AgentKeys.p_email];
    const templateName = emailTemplateConfiguration?.[EmailTemplateConfigurationKeys.templateName];
    const emailTemplate = this.isDynamicTemplateName(templateName)
      ? this.buildDynamicEmailTemplateModel(templateName)
      : EmailTemplatesDetailsMap.get(templateName as EmailTemplates);

    this.testEmailFormData = {
      to: [primaryEmail].filter(Boolean),
      emailTemplate,
      emailTemplateConfiguration,
      payload: null,
    };

    this.sendTestEmailModalComponent.showModal();
  };

  protected sendToEmail = async () => {
    const sent = await this.conferenceEmailTestEmailService.sendToEmail(this.testEmailFormData, this.conference);
    if (sent) {
      this.sendTestEmailModalComponent.forceCloseModal();
    }
  };

  protected emailTemplateDisplayExpr = item => {
    const template = item?.templateName;
    if (this.isDynamicTemplateName(template)) {
      return this.getDynamicTemplateTitle(template);
    }
    const templateConfiguration = EmailTemplatesDetailsMap.get(template as EmailTemplates);
    const title = templateConfiguration?.title;

    return title;
  };

  protected setTemplateCellValue = (updatedData, value) => {
    const eventName = this.conference?.[ConferenceKeys.eventName];
    const templateTitle = this.isDynamicTemplateName(value)
      ? this.getDynamicTemplateTitle(value)
      : EmailTemplatesDetailsMap.get(value as EmailTemplates)?.title;

    Object.assign(updatedData, {
      [EmailTemplateConfigurationKeys.templateName]: value,
      [EmailTemplateConfigurationKeys.subject]: [eventName, templateTitle].filter(Boolean).join(' - '),
    });
  };

  protected addEmailTemplate = (): void => {
    this.emailTemplateConfigurationFormService.getFormData(this.conference);
    this.editEmailTemplateModalComponent.showModal();
  };

  protected editEmailTemplate = ({ row: { data } }): void => {
    this.emailTemplateConfigurationFormService.getFormData(this.conference, data);
    this.editEmailTemplateModalComponent.showModal();
  };

  protected handleSaveEmailTemplateConfiguration = async () => {
    const cancel = await this.emailTemplateConfigurationFormService.handleSaveEmailTemplateConfiguration();

    if (cancel) {
      return;
    }

    this.editEmailTemplateModalComponent.forceCloseModal();
    this.emailTemplatesDataSource?.reload();
  };

  protected onRowRemoving = (e: RowRemovingEvent) => {
    this.emailTemplateConfigurationFormService.onRowRemoving(this.conference, e);
  };

  protected canSendTestEmail = (e: ColumnButtonClickEvent<EmailTemplateConfiguration>) => {
    return !e?.row?.isEditing;
  };

  protected handelAddAttachment = (): void => {
    this.mediaUploaderComponent.showModal();
  };

  protected onMediaChanged = (media: AGMedia): void => {
    this.emailTemplateConfigurationFormService.addAttachment(media);
  };

  protected calculateFileSizeDisplayValue = e => {
    const bytes = e?.fileSize;
    return this._getFileSizeInMb(bytes).toFixed(2) + ' MB';
  };

  protected getRequiredFieldLabel = (field: TemplateRequiredFieldView, index: number): string => {
    if (field?.label) {
      return field.label;
    }
    const baseLabel = field?.type === TemplateRequiredFieldTypes.activitiesDay ? 'Activities Day' : 'Excursions Set';
    return index > 0 ? `${baseLabel} #${index + 1}` : baseLabel;
  };

  protected getRequiredFieldPath = (field: TemplateRequiredFieldView): Array<string> => {
    if (field?.isStatic) {
      return [EmailTemplateConfigurationKeys.payload, 'activitiesDay'];
    }
    return [EmailTemplateConfigurationKeys.payload, 'requiredFields', field.widgetId, field.type];
  };

  protected isActivitiesTemplate = (value: EmailTemplates | string | null | undefined): boolean => {
    return [
      EmailTemplates.notifyEmailInviteesActivities,
      EmailTemplates.notifyEmailInviteesActivitiesFirstDay,
      EmailTemplates.notifyEmailInviteesActivitiesSecondDay,
    ].includes(value as EmailTemplates);
  };

  private async loadDynamicEmailTemplates() {
    try {
      const templates = await firstValueFrom(
        this.templatesService.getList([
          new QueryParam(TemplateKeys.type, WhereFilterOperandKeys.equal, TemplateTypes.email),
          new QueryParam(TemplateKeys.optInGroup, WhereFilterOperandKeys.equal, NotificationOptInGroupType.registrant),
        ]),
      );

      const dynamicTemplates = (templates ?? []).filter(
        template => template?.[BaseModelKeys.dbId] && template?.[TemplateKeys.name],
      );

      this.dynamicTemplatesMap = new Map(dynamicTemplates.map(template => [template?.[BaseModelKeys.dbId], template]));
      this.emailTemplateConfigurationFormService.setDynamicTemplatesMap(this.dynamicTemplatesMap);

      const dynamicItems = dynamicTemplates.map(template => ({
        value: this.toDynamicTemplateValue(template?.[BaseModelKeys.dbId]),
        key: 'Template Builder',
        description: template?.[TemplateKeys.name] ?? template?.[BaseModelKeys.dbId],
      }));

      const dynamicGroup = dynamicItems.length ? [{ key: 'Template Builder', items: dynamicItems }] : [];

      this.emailTemplatesLookup = [...EVENTS_EMAIL_TEMPLATES, ...dynamicGroup];
      this.emailTemplatesLookupFlat = this.flattenEmailTemplatesLookup(this.emailTemplatesLookup);
    } catch (error) {
      console.warn('Failed to load dynamic email templates.', error?.message ?? error);
    } finally {
      this.dynamicTemplatesLoaded$.next(true);
      this.emailTemplatesDataSource?.reload();
    }
  }

  private isDynamicTemplateName(value: unknown): value is string {
    return typeof value === 'string' && value.startsWith(this.dynamicTemplatePrefix);
  }

  private toDynamicTemplateValue(id: string | null | undefined): string {
    return `${this.dynamicTemplatePrefix}${id ?? ''}`;
  }

  private getDynamicTemplateId(value: string | null | undefined): string | null {
    if (!value || !this.isDynamicTemplateName(value)) {
      return null;
    }
    const id = value.slice(this.dynamicTemplatePrefix.length).trim();
    return id || null;
  }

  private getDynamicTemplateTitle(value: string | null | undefined): string | undefined {
    const id = this.getDynamicTemplateId(value);
    if (!id) {
      return undefined;
    }
    return this.dynamicTemplatesMap.get(id)?.[TemplateKeys.name];
  }

  private buildDynamicEmailTemplateModel(value: string): EmailTemplateModel {
    const title = this.getDynamicTemplateTitle(value) ?? 'Template Builder';
    return {
      title,
      model: EmailTemplatesModels.registrant,
    } as EmailTemplateModel;
  }

  private _getFileSizeInMb = (bytes: number): number => {
    if (isNaN(bytes) || bytes === 0) {
      return 0;
    }
    return bytes / Math.pow(1024, 2);
  };

  private ensureRequiredFieldsPayload(
    fields: TemplateRequiredFieldView[],
    formData: EmailTemplateConfiguration | null | undefined,
  ) {
    if (!fields?.length || !formData) {
      return;
    }

    const payloadExists = !!formData?.[EmailTemplateConfigurationKeys.payload];
    const payload = formData?.[EmailTemplateConfigurationKeys.payload] ?? {};
    const dynamicWidgetIds = fields
      .filter(field => !field?.isStatic && !!field?.widgetId)
      .map(field => field.widgetId)
      .filter((widgetId): widgetId is string => !!widgetId);
    const requiredFields = this.ensureRecord(payload.requiredFields);
    const requiredFieldsWasMissing = payload.requiredFields !== requiredFields;
    payload.requiredFields = requiredFields;
    const missingWidgetIds = dynamicWidgetIds.filter(widgetId => !requiredFields[widgetId]);

    missingWidgetIds.forEach(widgetId => {
      requiredFields[widgetId] = {};
    });

    const changed = !payloadExists || requiredFieldsWasMissing || missingWidgetIds.length > 0;
    if (!changed) {
      return;
    }

    formData[EmailTemplateConfigurationKeys.payload] = payload;
  }

  private ensureRecord(value: unknown): Record<string, any> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {};
  }

  private flattenEmailTemplatesLookup(
    lookup: Array<{ key: string; items: Array<{ value: EmailTemplates | string; description: string }> }>,
  ): Array<{ value: EmailTemplates | string; description: string }> {
    return (lookup ?? []).flatMap(group => {
      return (group?.items ?? []).map(item => ({
        [LookupKeys.value]: item?.value,
        [LookupKeys.description]: item?.description,
      }));
    });
  }
}
